import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LockedFeature } from "@/components/LockedFeature";
import { toast } from "sonner";
import { Map, Loader2, CheckCircle2, Circle, Target, Wrench, BookOpen, Flag, Plus, History } from "lucide-react";

const PHASE_COLORS = ["#0055FF", "#10B981", "#F59E0B", "#A855F7", "#EC4899"];

export default function FounderRoadmap() {
  const { user } = useAuth();
  const isPro = user?.is_pro || user?.tier === "pro";
  const [goal, setGoal] = useState("");
  const [industry, setIndustry] = useState("");
  const [experience, setExperience] = useState("Beginner");
  const [loading, setLoading] = useState(false);
  const [roadmap, setRoadmap] = useState(null);
  const [past, setPast] = useState([]);

  const loadPast = () => api.get("/roadmaps").then((r) => setPast(r.data)).catch(() => {});
  useEffect(() => { if (isPro) loadPast(); }, [isPro]);

  if (!isPro) return (
    <LockedFeature title="Founder Roadmap Generator" description="Get a personalized 5-phase roadmap — Foundations to Scaling — with tasks, skills, resources and milestones, tailored to your exact goal."
      perks={["Tailored to your goal & industry", "Tasks, skills, resources, milestones", "Track your completion across phases"]} />
  );

  const generate = async () => {
    if (!goal.trim()) return;
    setLoading(true); setRoadmap(null);
    try { const { data } = await api.post("/roadmaps", { goal, industry, experience }); setRoadmap(data); loadPast(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not generate roadmap"); }
    finally { setLoading(false); }
  };

  const toggleTask = async (task) => {
    const { data } = await api.post(`/roadmaps/${roadmap.id}/toggle`, { task });
    setRoadmap({ ...roadmap, completed: data.completed });
  };

  const totalTasks = roadmap ? roadmap.phases.reduce((a, p) => a + (p.tasks?.length || 0), 0) : 0;
  const doneTasks = roadmap?.completed?.length || 0;
  const percent = totalTasks ? Math.round((doneTasks / totalTasks) * 100) : 0;

  return (
    <div className="space-y-8" data-testid="roadmap-page">
      <div>
        <span className="font-mono-eco text-xs uppercase tracking-widest text-slate-500">Founder Roadmap</span>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-white tracking-tight mt-1">Your path, mapped phase by phase.</h1>
        <p className="text-slate-400 mt-2">Tell ECO your goal and get a personalized 5-phase roadmap you can track.</p>
      </div>

      <div className="eco-card p-6 space-y-4">
        <input data-testid="roadmap-goal" value={goal} onChange={(e) => setGoal(e.target.value)} placeholder="Your goal (e.g., Build a SaaS, Launch an AI product)" className="eco-input" />
        <div className="grid sm:grid-cols-2 gap-3">
          <input data-testid="roadmap-industry" value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder="Industry (optional)" className="eco-input" />
          <select data-testid="roadmap-experience" value={experience} onChange={(e) => setExperience(e.target.value)} className="eco-input">
            {["Beginner", "Intermediate", "Advanced"].map((x) => <option key={x} value={x} className="bg-[#0A0A0A]">{x}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="roadmap-generate-btn" onClick={generate} disabled={loading || !goal.trim()} className="btn-accent">{loading ? <Loader2 size={16} className="animate-spin" /> : <><Map size={16} /> Generate roadmap</>}</button>
          {past.length > 0 && <span className="text-xs text-slate-500 flex items-center gap-1"><History size={13} /> {past.length} saved</span>}
        </div>
        {past.length > 0 && !roadmap && (
          <div className="flex flex-wrap gap-2 pt-2 border-t border-white/5">
            {past.slice(0, 6).map((r) => <button key={r.id} onClick={() => setRoadmap(r)} className="text-xs text-slate-300 border border-white/10 rounded-full px-3 py-1.5 hover:bg-white/5">{r.goal}</button>)}
          </div>
        )}
      </div>

      {roadmap && (
        <div className="space-y-5" data-testid="roadmap-result">
          <div className="eco-card p-5 flex items-center justify-between">
            <div><div className="font-display text-lg font-semibold text-white">{roadmap.goal}</div><div className="text-xs text-slate-500">{roadmap.industry || "General"} · {roadmap.experience}</div></div>
            <div className="text-right"><div className="font-display text-2xl font-bold text-white">{percent}%</div><div className="text-[10px] font-mono-eco uppercase tracking-widest text-slate-500">{doneTasks}/{totalTasks} tasks</div></div>
          </div>
          {roadmap.phases.map((phase, i) => (
            <div key={i} className="eco-card p-6" data-testid={`phase-${i}`}>
              <div className="flex items-center gap-3 mb-3">
                <span className="h-8 w-8 rounded-lg flex items-center justify-center font-display font-bold text-white" style={{ background: PHASE_COLORS[i % 5] }}>{i + 1}</span>
                <h3 className="font-display text-lg font-semibold text-white">{phase.name}</h3>
              </div>
              <p className="text-sm text-slate-400 mb-4">{phase.focus}</p>
              <div className="space-y-2 mb-4">
                {(phase.tasks || []).map((t) => {
                  const done = roadmap.completed?.includes(t);
                  return (
                    <button key={t} onClick={() => toggleTask(t)} data-testid="roadmap-task" className="w-full text-left flex items-center gap-3 p-2.5 rounded-lg hover:bg-white/5">
                      {done ? <CheckCircle2 size={18} className="text-emerald-400 shrink-0" /> : <Circle size={18} className="text-slate-600 shrink-0" />}
                      <span className={`text-sm ${done ? "text-slate-500 line-through" : "text-slate-200"}`}>{t}</span>
                    </button>
                  );
                })}
              </div>
              <div className="grid sm:grid-cols-3 gap-3 text-sm">
                <MiniList icon={Wrench} title="Skills" items={phase.skills} />
                <MiniList icon={BookOpen} title="Resources" items={phase.resources} />
                <MiniList icon={Flag} title="Milestones" items={phase.milestones} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MiniList({ icon: I, title, items }) {
  if (!items?.length) return null;
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-3">
      <div className="flex items-center gap-1.5 text-[10px] font-mono-eco uppercase tracking-widest text-slate-500 mb-2"><I size={12} /> {title}</div>
      <ul className="space-y-1">{items.map((x, i) => <li key={i} className="text-xs text-slate-300">• {x}</li>)}</ul>
    </div>
  );
}
